/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import {
    IPersonaCoinStyleProps,
    IPersonaCoinStyles,
    IPersonaProps,
    IPersonaStyles,
    Persona,
    PersonaSize
} from '@fluentui/react/lib/Persona';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisContextualMenu, LuisContextualMenuProps } from 'src/core/components/LuisContextualMenu';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { AzureSwitchTenantModal } from 'src/modules/azure/components/AzureSwitchTenantModal';

const messages = defineMessages({
    select: { id: 'portal.profileDropdown.select', defaultMessage: 'Select' },
    signOutLink: { id: 'portal.profileDropdown.signOutLink', defaultMessage: 'Sign out' },
    viewAccount: { id: 'portal.profileDropdown.viewAccount', defaultMessage: 'View account' },
    currentResource: { id: 'portal.profileDropdown.currentResource', defaultMessage: 'Current resource' },
    currentDirectory: { id: 'portal.profileDropdown.currentDirectory', defaultMessage: 'Current directory' },
    noResourceSelected: { id: 'portal.profileDropdown.noResourceSelected', defaultMessage: 'No resource selected' },
    switchAccount: { id: 'portal.profileDropdown.switchAccount', defaultMessage: 'Sign in with a different account' },
    currentRole: { id: 'portal.profileDropdown.currentRole', defaultMessage: 'Current role' },
    learnMoreAboutRole: {
        id: 'portal.profileDropdown.learnMoreAboutRole',
        defaultMessage: 'This is your assigned role on the selected authoring resource.'
    }
});

const minimumRoleLengthToBreakLine: number = 20;
const theme = getScopedTheme('GlobalProfileDropdown');

const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { fontSize: '13px' } });
const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { fontSize: '13px' } });
const signInIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '18px', padding: '10px', border: `1px solid ${theme.defaultTextColor}`, borderRadius: '50%' }
});
const personaCoinStyles = classNamesFunction<IPersonaCoinStyleProps, IPersonaCoinStyles>()({ initials: { fontWeight: 'bold' } });
const personaStyles = classNamesFunction<IPersonaProps, IPersonaStyles>()({
    root: { padding: '0 2px', display: 'block', marginTop: '10px' }
});
const signInButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    flexContainer: { justifyContent: 'center' },
    root: { width: '100%', height: 'auto', backgroundColor: theme.signInButtonBackground }
});
const calloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: 'auto' },
    root: { marginTop: '1px', boxShadow: theme.boxShadow, color: theme.defaultTextColor }
});

const { getClassName } = getStylistV2('GlobalProfileDropdown');
export const learnMoreAboutRoleIconClassName = getClassName('learnMoreAboutRoleIconClassName', { fontSize: '13px' });

export const GlobalProfileDropdown = observer((props: Omit<LuisContextualMenuProps, 'items'>) => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore, uiStore } = useGlobalState();

    const user = rootStore.userStore;
    const isLanguageStudio = uiStore.portal.kind === 'languageStudio';
    const currentResource = rootStore.azureStore.session.csAccountStore;
    const currentTenant = rootStore.azureStore.tenantStoreHouse.findById(rootStore.azureStore.session.tenantId);

    const { displayedUserRole } = rootStore.serviceKey;

    const [isSwitchTenantModalHidden, setIsSwitchTenantModalHidden] = React.useState(true);

    return (
        <Stack>
            <LuisContextualMenu
                {...props}
                contextualMenuProps={{ calloutProps: { styles: calloutStyles }, ...props.contextualMenuProps }}
                items={[
                    {
                        key: 'signOut',
                        onRender: () => (
                            <Stack horizontal horizontalAlign="end" tokens={{ padding: '0 7px' }}>
                                <ActionButton
                                    styles={buttonStyles}
                                    text={localize(messages.signOutLink)}
                                    onClick={() => dispatcher.dispatch('logout')}
                                />
                            </Stack>
                        )
                    },
                    {
                        key: 'profile',
                        onRender: () => (
                            <Stack horizontal tokens={{ childrenGap: 13, padding: '0 16px 16px 25px' }}>
                                <Persona
                                    styles={personaStyles}
                                    size={PersonaSize.size72}
                                    imageInitials={user.initials}
                                    coinProps={{ styles: personaCoinStyles }}
                                    initialsColor={theme.avatarBackgroundColor}
                                    imageUrl={`data:image/jpeg;base64, ${user.photo}`}
                                />

                                <Stack tokens={{ childrenGap: 20 }}>
                                    <Stack tokens={{ childrenGap: 4 }}>
                                        <LuisText variant="large" fontWeight={700}>
                                            {user.name}
                                        </LuisText>
                                        <LuisText styles={textStyles}>{user.email}</LuisText>
                                    </Stack>

                                    <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 40 }}>
                                        <Stack>
                                            <LuisText styles={textStyles}>{localize(messages.currentResource)}</LuisText>
                                            <TruncatedText styles={textStyles}>
                                                {currentResource?.name ?? localize(messages.noResourceSelected)}
                                            </TruncatedText>
                                            {currentResource && (
                                                <LuisText variant="xSmall" color={theme.secondaryTextColor}>
                                                    ({currentResource?.region}, {currentResource?.sku.code})
                                                </LuisText>
                                            )}
                                        </Stack>

                                        <Link
                                            onClick={() =>
                                                dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })
                                            }>
                                            {localize(messages.select)}
                                        </Link>
                                    </Stack>

                                    {displayedUserRole.toLocaleLowerCase() !== 'none' && (
                                        <Stack verticalAlign="center">
                                            <Stack horizontal verticalAlign="baseline">
                                                <LuisText styles={textStyles}>{localize(messages.currentRole)}</LuisText>
                                                <CalloutButton
                                                    isDarkMode={false}
                                                    content={localize(messages.learnMoreAboutRole)}
                                                    iconClassName={learnMoreAboutRoleIconClassName}
                                                />
                                            </Stack>
                                            <Stack
                                                tokens={{ childrenGap: '4px' }}
                                                horizontal={displayedUserRole.length <= minimumRoleLengthToBreakLine}>
                                                <LuisText styles={textStyles}>{displayedUserRole}</LuisText>
                                                <LuisText>
                                                    <ExternalLink
                                                        href={
                                                            isLanguageStudio
                                                                ? GlobalHeaderLinks.learnMoreAboutStdioRBACLink
                                                                : GlobalHeaderLinks.learnMoreAboutLUISRBACLink
                                                        }>
                                                        {localize(globalMessages.learnMore)}
                                                    </ExternalLink>
                                                </LuisText>
                                            </Stack>
                                        </Stack>
                                    )}

                                    <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 40 }}>
                                        <Stack>
                                            <LuisText styles={textStyles}>{localize(messages.currentDirectory)}</LuisText>
                                            <LuisText styles={textStyles}>{currentTenant?.name}</LuisText>
                                        </Stack>
                                        <Link onClick={() => setIsSwitchTenantModalHidden(false)}>{localize(messages.select)}</Link>
                                    </Stack>

                                    <ExternalLink href={GlobalHeaderLinks.viewAccountLink}>{localize(messages.viewAccount)}</ExternalLink>
                                </Stack>
                            </Stack>
                        )
                    },
                    {
                        key: 'switchAccount',
                        onRender: () => (
                            <ActionButton styles={signInButtonStyles} onClick={() => dispatcher.dispatch('login', { selectAccount: true })}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20, padding: '9px 24px' }}>
                                    <Icon iconName="AddFriend" styles={signInIconStyles} />
                                    <LuisText>{localize(messages.switchAccount)}</LuisText>
                                </Stack>
                            </ActionButton>
                        )
                    }
                ]}
            />

            <AzureSwitchTenantModal
                hidden={isSwitchTenantModalHidden}
                onSubmit={() => setIsSwitchTenantModalHidden(true)}
                onDismiss={() => setIsSwitchTenantModalHidden(true)}
            />
        </Stack>
    );
});
